// English UI strings, namespaced by component/page. Read in components via
// `const t = useTranslation();` then `t.<namespace>.<key>` (or
// `t.<namespace>.<key>(arg)` for the interpolated entries that are functions).
//
// This is the source-of-truth bundle: es/strings.js starts as a verbatim copy
// of this file and is translated separately. Only strings rendered by the app
// itself live here — values that come from the server (team/player/league/group
// names, match statuses, server error messages) are intentionally absent.
export default {
  common: {
    appName: "Gol Predictor",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    close: "Close",
    copy: "Copy",
    share: "Share",
    join: "Join",
    // Score/result not yet available.
    tbd: "TBD",
    // `zone` is the local time-zone abbreviation, e.g. "PDT".
    localTimeNote: zone =>
      `All times are shown in your local time zone (${zone}).`,
  },

  header: {
    activeGroup: "Active Group:",
    joinGroup: "+ Join a group",
    leaveGroup: "Leave this group",
    signOut: "Sign Out",
    newMessages: "New Messages",
    newMessagesCount: n => `${n} New Messages`,
    sendNewMessage: "Send New Message",
    notificationReminder: "Do not forget to save",
  },

  sidebar: {
    leagues: "Leagues",
    calendar: "Calendar",
    leaderboards: "Leaderboards",
    admin: "Admin",
  },

  login: {
    login: "Login",
    username: "Username",
    password: "Password",
    loginError: "Something is wrong with your login or password :(",
    badPassword: "Bad password",
  },

  joinGroup: {
    title: "Join a group",
    groupName: "Group name",
    passcode: "Passcode",
    joinError: "Could not join group",
  },

  layout: {
    groupsError: "Something went wrong loading your groups. Please try again later.",
    noGroupsTitle: "You're not part of any groups.",
    noGroupsBody: "Ask an admin to add you to a group, or join one with a passcode.",
    joinGroup: "Join a group",
    leaveGroupTitle: "Leave group",
    leaveGroupConfirm: group => `Are you sure you want to leave "${group}"?`,
    groupLeft: "You left the group",
    couldNotLeaveGroup: "Could not leave group",
  },

  fixtures: {
    date: "Date",
    teams: "Teams",
    status: "Status",
    prediction: "Prediction",
    actualScore: "Actual Score",
    points: "Points",
    noFixtures: "No fixtures found",
    savedSnackbar: "Your predictions have been saved",
    couldNotSave: "Could not save",
    couldNotLoad: "Could not load fixtures",
    couldNotGetPredictions: "Could not get predictions",
    selectAll: "Select all",
    clearSelection: "Clear",
    hideScheduled: "Hide scheduled",
    copyToGroup: "Copy to group…",
    copyTitle: "Copy predictions to another group",
    copyTargetLabel: "Copy to groups",
    // `existing` is the target group's current prediction, e.g. "2 - 1".
    copyConflict: existing => `Already predicted: ${existing}`,
    // `count` is how many selected matches had no prediction to copy.
    copySkipped: count =>
      `${count} selected ${count === 1 ? "match has" : "matches have"} no prediction and ${count === 1 ? "was" : "were"} skipped.`,
    copying: "Copying…",
    copied: "Predictions copied",
    couldNotCopy: "Could not copy predictions",
    noEligibleGroups:
      "You're not in any other group that has this league.",
    nothingToCopy:
      "None of the selected matches have a prediction to copy.",
  },

  calendar: {
    title: "Calendar",
    showAllPredictions: "Show all predictions",
    liveNow: "Live now",
    matches: "Matches",
    noMatches: "No matches on this day.",
    today: "Today",
    viewPredictions: "View predictions",
    editPredictions: "Edit predictions",
    scheduled: "Scheduled",
    missingPredictionsTitle: "Missing predictions",
    couldNotLoad: "Could not load calendar",
    couldNotSave: "Could not save predictions",
    copied: "Predictions copied to clipboard",
    saved: "Predictions saved",
  },

  // Labels in the plain-text share/clipboard output (pasted into chat) and the
  // live/finished score readouts. Surrounding punctuation/layout stays in code;
  // only these words are translated, e.g. "(LIVE - 45')", "(Reg: 1 - 1)",
  // "(5pts: 5CR)".
  share: {
    liveLabel: "LIVE",
    regLabel: "Reg",
    ptsSuffix: "pts",
  },

  // The shared per-match predictions table (calendar dialog + inline expansion).
  predictions: {
    noPredictions: "No predictions for this match.",
    player: "Player",
    prediction: "Prediction",
    points: "Points",
    pointsInfo: "Points Info",
  },

  leaderboards: {
    title: "Leaderboards",
    aggregate: "Aggregate",
    // Uppercase header used in the shared plain-text copy.
    aggregateHeader: "AGGREGATE",
    rank: "Rank",
    player: "Player",
    settled: "Settled",
    live: "Live",
    total: "Total",
    points: "Points",
    noStandings: "No standings yet",
    copied: "Leaderboard copied to clipboard",
    couldNotLoad: "Could not load leaderboards",
  },

  admin: {
    title: "Admin",
    exportTitle: "Export Past Predictions",
    exportDescription:
      "Download a CSV of every player's predictions for past matches across all leagues in this group.",
    exporting: "Exporting…",
    couldNotExport: "Could not export predictions",
    exportMissingTitle: "Export Missing Predictions",
    exportMissingDescription:
      "Download a CSV listing, per match, the group players who have no prediction.",
    exportMissingDialogTitle: "Export Missing Predictions",
    exportMissingPast: "Past / locked matches",
    exportMissingFuture: "Future matches",
    couldNotExportMissing: "Could not export missing predictions",
    scoringTitle: "Scoring Mode",
    scoringDescription:
      "Choose how predictions are scored for this group. Changing it recalculates points for all matches.",
    scoringLabel: "Scoring mode",
    saving: "Saving…",
    scoringSaved: "Scoring mode updated",
    couldNotSaveScoring: "Could not update scoring mode",
    usersTitle: "Manage Users",
    usersDescription:
      "Add players to this group and set their login passwords. Passwords are entered in plain text and stored securely hashed.",
    addUserButton: "Add User",
    addUserTitle: "Add User",
    addUserLabel: "Username",
    userAdded: "User added",
    couldNotAddUser: "Could not add user",
    changePasswordButton: "Change password",
    changePasswordTitle: member => `Set password for ${member}`,
    passwordLabel: "New password",
    passwordSet: "Password updated",
    couldNotSetPassword: "Could not set password",
    removeMemberButton: "Remove",
    removeMemberConfirm: member => `Remove ${member} from this group?`,
    memberRemoved: "Member removed",
    couldNotRemoveMember: "Could not remove member",
    noMembers: "No members yet",
    loadMembersError: "Could not load members",
    // CSV column headers, in the order written to the file.
    csvColumns: [
      "league",
      "match date",
      "home team",
      "away team",
      "home score",
      "away score",
      "player name",
      "home prediction",
      "away prediction",
      "points",
      "points info",
    ],
    // CSV column headers for the missing-predictions export.
    csvMissingColumns: [
      "league",
      "match date",
      "home team",
      "away team",
      "home score",
      "away score",
      "missing predictions",
    ],
  },
};
